import type { LinkProps } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { localizeHref } from '@/paraglide/runtime';

export function LinksSrOnly() {
  return (
    <div
      aria-hidden='true'
      className='absolute -z-10 h-0 overflow-hidden opacity-[0.01]'
      role='presentation'
    >
      <Link href='/audio'>Audio</Link>
      <Link href='/college'>College</Link>
      <Link href='/contact-us'>Contact Us</Link>
      <Link href='/delete-data'>Delete Data</Link>
      <Link href='/academic'>Academic</Link>
      <Link href='/newsletter'>Newsletter</Link>
      <Link href='/password-reset'>Password Reset</Link>
      <Link href='/post-purchase'>Post Purchase</Link>
      <Link href='/ref'>Referral</Link>
      <Link href='/refund-policy'>Refund Policy</Link>
      <Link href='/survey'>Survey</Link>
      <Link href='/terms'>Terms</Link>
      <Link href='/youtube'>YouTube</Link>
    </div>
  );
}

const CLASS_NAME =
  'pointer-events-none block text-[0px] leading-[0px] no-underline select-none';

/**
 * `href` is `LinkProps['to']` — the route tree's path union, not `string` — so
 * a link to a page that never existed or has since been deleted fails the
 * typecheck instead of shipping a crawlable 404. That is the whole point of
 * this block: it exists to be crawled, so a dead entry is worse than none.
 *
 * Renders a plain `<a>` rather than TanStack's `<Link>` on purpose. These are
 * `aria-hidden`, `tabIndex={-1}` crawl targets, so router prefetch and
 * client-side nav are dead weight; `localizeHref` gives the same locale
 * rewriting the router would have applied.
 */
function Link({
  href,
  children,
}: { href: LinkProps['to'] } & PropsWithChildren) {
  return (
    <a className={CLASS_NAME} href={localizeHref(href ?? '/')} tabIndex={-1}>
      {children}
    </a>
  );
}
